// src/hooks/useOrgSettings.js
import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import useOrgClaims from './useOrgClaims';

/**
 * Returns: { orgId, settings, companyName, loading }
 * - Subscribes to /organizations/{orgId} once orgId is known.
 * - No auth token refreshes here (prevents quota issues).
 */
export default function useOrgSettings() {
  const { orgId, loading: claimsLoading } = useOrgClaims();
  const [state, setState] = useState({
    settings: null,
    loading: true,
  });

  useEffect(() => {
    if (claimsLoading) return; // wait for claims
    if (!orgId) {
      setState({ settings: null, loading: false });
      return;
    }
    const ref = doc(db, `organizations/${orgId}`);
    const unsub = onSnapshot(
      ref,
      (snap) => setState({ settings: snap.exists() ? snap.data() : null, loading: false }),
      () => setState({ settings: null, loading: false })
    );
    return () => unsub();
  }, [orgId, claimsLoading]);

  const companyName = state.settings?.companyName || '—';

  return useMemo(
    () => ({
      orgId,
      settings: state.settings,
      companyName,
      loading: claimsLoading || state.loading,
    }),
    [orgId, state.settings, companyName, claimsLoading, state.loading]
  );
}
